/**
 * The builder's dictionaries (feature 0051 lot 4).
 *
 * Flat `key → phrase`, the same shape the dashboard uses, for the same reason: a missing key has
 * exactly one failure mode and `Object.keys()` is the completeness test.
 *
 * There is no English file. English is what the schemas already say, and `translateFields` falls back
 * to the literal in the schema — so an English reader is served by the code itself, and the day a
 * label is reworded there is one place to change instead of two that can drift.
 *
 * Lot 7 (13/09) added Spanish and made the map GENERATED, for the reason the dashboard's loaders gave:
 * a list maintained by hand next to a directory of files is a list that eventually disagrees with it.
 *
 * ## Why this is a REGISTRY that fills, and not a map of dictionaries (14/09)
 *
 * The seller reads the editor in ONE language. Importing all six to serve one put ~1.4 MB of source
 * into the entry chunk — a fifth of the cold-load bytes, spent on five languages nobody in that tab
 * will ever read. So the dictionaries are chunks now, and this object starts EMPTY and is filled by
 * `loadBuilderDictionary()`, which `main.tsx` awaits before the first render.
 *
 * `lookup()` in `schema-text.ts` reads this object at CALL time, never at module scope, which is what
 * makes the swap invisible to the ~2 400 call sites: by the time anything renders, the one dictionary
 * that matters is in. A locale whose chunk fails to load leaves the registry empty and the editor
 * renders its English floor — the state it was in before any of this existed, never a blank screen.
 */
import type { BuilderLocale } from './locale';
import { builderLocale } from './locale';
import { LOADERS } from './dictionaries.gen';

export type BuilderDictionary = Readonly<Record<string, string>>;

const loaded: Partial<Record<BuilderLocale, BuilderDictionary>> = {};

/** The dictionaries that have been LOADED. Read at call time — see the note above. */
export const DICTIONARIES: Partial<Record<BuilderLocale, BuilderDictionary>> = loaded;

/**
 * Fetch the reader's dictionary. Awaited once, in `main.tsx`, before `createRoot().render()` — so the
 * FIRST paint is already translated and no label ever flips from English to French under the seller.
 *
 * Never rejects: English is the floor, and a chunk that fails to arrive (a stale cached `index.html`
 * pointing at a hash a deploy has since replaced, a flaky connection) must not stop the editor opening.
 */
export async function loadBuilderDictionary(
  locale: BuilderLocale = builderLocale(),
): Promise<void> {
  if (loaded[locale]) return;
  const load = LOADERS[locale];
  // English, or a language we have no file for: `translateFields` falls back to the schema literal.
  if (!load) return;
  try {
    loaded[locale] = await load();
  } catch {
    /* see the doc above — the English floor is a complete editor, not a failure state */
  }
}

/**
 * Test seam: put a dictionary in the registry synchronously. `src/test/setup.ts` registers all of
 * them, so a component test renders in whatever language `__setBuilderLocaleForTests` asks for
 * without every test file having to await a chunk. Never app code.
 */
export function __registerDictionaryForTests(
  locale: BuilderLocale,
  dictionary: BuilderDictionary,
): void {
  loaded[locale] = dictionary;
}
